import { Component } from "react";
import axios from "axios";

/**
 * AppErrorBoundary — last-line-of-defense to prevent a single
 * runtime JS error from white-screening the entire app.
 *
 * Iter 161 additions:
 *  - Technical details are now expanded by default so the user can
 *    read the actual error instead of hunting for a "Details" toggle.
 *  - "Copy details" button dumps a formatted crash report to
 *    clipboard so the user can paste it into Slack/support.
 *  - Every crash is POSTed to `/api/telemetry/client-error` so the
 *    backend log stream shows what's crashing in production — even
 *    when the user just hits reload and moves on.
 *
 * We keep the boundary flexible: `sectionMode` renders a compact
 * fallback that only replaces the crashed subtree instead of the
 * whole page, so drawer subpanels can crash independently.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, errorInfo);
    this.setState({ errorInfo });
    // Best-effort ship to backend telemetry. Silently swallow if it
    // fails — we're already in a broken state, we don't want a
    // networking failure here to compound the issue.
    try {
      const authTok =
        localStorage.getItem("pff_auth_token") ||
        localStorage.getItem("pff_portal_token") ||
        "";
      // Peek at the user email from a saved auth cache if we have one.
      let userEmail = null;
      try {
        const cached = JSON.parse(localStorage.getItem("pff_auth_user") || "null");
        userEmail = cached?.email || null;
      } catch { /* ignore */ }
      axios.post(
        `${API}/telemetry/client-error`,
        {
          message: String(error?.message || error || "").slice(0, 2000),
          stack: String(error?.stack || "").slice(0, 8000),
          component_stack: String(errorInfo?.componentStack || "").slice(0, 8000),
          url: window.location.href.slice(0, 500),
          user_agent: navigator.userAgent.slice(0, 400),
          user_email: userEmail,
        },
        authTok ? { headers: { Authorization: `Bearer ${authTok}` } } : undefined,
      ).catch(() => {});
    } catch { /* ignore */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };

  handleCopy = async () => {
    const report = [
      `URL: ${window.location.href}`,
      `Message: ${this.state.error?.message || "(no message)"}`,
      `Stack:\n${this.state.error?.stack || "(no stack)"}`,
      `Component stack:\n${this.state.errorInfo?.componentStack || "(no component stack)"}`,
      `User-Agent: ${navigator.userAgent}`,
      `Timestamp: ${new Date().toISOString()}`,
    ].join("\n\n");
    try {
      await navigator.clipboard.writeText(report);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch { /* ignore */ }
  };

  renderSectionFallback() {
    // Compact inline fallback used when this boundary sits around a
    // subtree (e.g. one drawer section) instead of the whole app.
    const msg = this.state.error?.message || "Something went wrong.";
    return (
      <div
        className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 my-3"
        data-testid="section-error-boundary"
      >
        <p className="text-[10px] font-mono uppercase tracking-widest text-red-400 mb-1">
          / section crashed
        </p>
        <p className="text-sm text-red-300 font-semibold mb-2">
          This panel hit an error and couldn't render.
        </p>
        <p className="text-xs text-red-200/70 mb-3 break-words">
          {msg}
        </p>
        <div className="flex gap-2">
          <button
            onClick={this.handleReload}
            data-testid="section-error-reload"
            className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full bg-red-500/20 text-red-300 hover:bg-red-500/30"
          >
            Reload page
          </button>
          <button
            onClick={this.handleCopy}
            data-testid="section-error-copy"
            className="text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/5 text-zinc-300 hover:bg-white/10"
          >
            {this.state.copied ? "Copied" : "Copy details"}
          </button>
        </div>
      </div>
    );
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.sectionMode) {
      return this.renderSectionFallback();
    }

    const msg = this.state.error?.message || "Something went wrong.";
    const stack = this.state.error?.stack || "";
    const compStack = this.state.errorInfo?.componentStack || "";

    return (
      <div
        className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center p-6"
        data-testid="app-error-boundary"
      >
        <div className="max-w-xl w-full bg-[#111] border border-white/10 rounded-2xl p-6">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#0052FF] mb-2">
            / SOMETHING BROKE
          </p>
          <h1 className="font-display text-2xl font-extrabold tracking-tight mb-2">
            We hit a snag.
          </h1>
          <p className="text-sm text-zinc-400 mb-4">
            The action you just took caused an error on this page. Your data
            is safe — nothing was lost. Reload the page to continue.
          </p>

          <div className="flex flex-wrap gap-2 mb-5">
            <button
              onClick={this.handleReload}
              data-testid="app-error-reload"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#00FFA3] text-black text-sm font-semibold hover:bg-white"
            >
              Reload page
            </button>
            <button
              onClick={this.handleCopy}
              data-testid="app-error-copy"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 text-zinc-200 text-sm font-semibold hover:bg-white/10 border border-white/10"
            >
              {this.state.copied ? "Copied ✓" : "Copy error details"}
            </button>
          </div>

          <div className="text-xs text-zinc-500">
            <p className="mb-1 font-mono uppercase tracking-widest text-[10px]">
              Error
            </p>
            <pre className="p-3 bg-[#0A0A0A] border border-red-500/20 rounded text-[11px] whitespace-pre-wrap break-words text-red-300 mb-3 max-h-32 overflow-auto">
              {msg}
            </pre>

            {stack && (
              <>
                <p className="mb-1 font-mono uppercase tracking-widest text-[10px]">
                  Stack
                </p>
                <pre className="p-3 bg-[#0A0A0A] border border-white/5 rounded text-[10px] whitespace-pre-wrap break-words text-zinc-400 mb-3 max-h-40 overflow-auto">
                  {stack}
                </pre>
              </>
            )}

            {compStack && (
              <>
                <p className="mb-1 font-mono uppercase tracking-widest text-[10px]">
                  Component tree
                </p>
                <pre className="p-3 bg-[#0A0A0A] border border-white/5 rounded text-[10px] whitespace-pre-wrap break-words text-zinc-500 max-h-40 overflow-auto">
                  {compStack}
                </pre>
              </>
            )}

            <p className="mt-3 text-[10px] text-zinc-600">
              This crash was auto-reported. Copy the details above and send
              them to support if it keeps happening.
            </p>
          </div>
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
